/**
 * Pure completion-key builders + day progress math (Phase 4).
 *
 * Operates on the verified `af_pt_plan_checked` flat map. Keys are additive to
 * the legacy schema (see trainingBridge.ts) so existing mid-plan data keeps
 * working. No I/O here — the adapter layer reads/writes localStorage and syncs.
 */
import type { PlanData, PlanDay } from '@/features/ai-plan/lib/types'
import type { CheckedMap, DayPosition } from '@/shared/bridge/trainingBridge'
import { getDayOfWeek } from '@/shared/utils/dateMath'  // to filter pre-start days in partial first week for plans with plan_start_date

export type PhaseName = 'warmup' | 'cooldown'

export function dayDoneKey(pos: DayPosition): string {
  return `w${pos.week}_d${pos.dayOfWeek}_done`
}
export function exerciseKey(pos: DayPosition, index: number): string {
  return `w${pos.week}_d${pos.dayOfWeek}_ex_${index}`
}
export function phaseKey(pos: DayPosition, phase: PhaseName): string {
  return `w${pos.week}_d${pos.dayOfWeek}_${phase}_done`
}

export interface DayProgress {
  done: number
  total: number
  pct: number
}

/**
 * Completion for a single day. Each present phase (warm-up, cool-down) and each
 * main exercise counts as one unit. Rest days, empty days, and missing day data
 * collapse to a single unit driven by the day-done flag.
 */
export function computeDayProgress(day: PlanDay | null, pos: DayPosition, checked: CheckedMap): DayProgress {
  const hasWarmup = (day?.warmup?.length ?? 0) > 0
  const hasCooldown = (day?.cooldown?.length ?? 0) > 0
  const mainCount = day?.exercises?.length ?? 0

  if (!day || day.is_rest || hasWarmup === false && hasCooldown === false && mainCount === 0) {
    const done = checked[dayDoneKey(pos)] ? 1 : 0
    return { done, total: 1, pct: done * 100 }
  }

  let total = mainCount
  let done = 0
  for (let i = 0; i < mainCount; i++) {
    if (checked[exerciseKey(pos, i)]) done++
  }
  if (hasWarmup) {
    total++
    if (checked[phaseKey(pos, 'warmup')]) done++
  }
  if (hasCooldown) {
    total++
    if (checked[phaseKey(pos, 'cooldown')]) done++
  }

  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  return { done, total, pct }
}

/**
 * Active template for a week, honoring the phase-2 boundary (week > ceil/2),
 * mirroring `getDay` so dates/status/navigation stay consistent.
 */
export function templateForWeek(plan: PlanData, week: number): PlanDay[] {
  const total = Math.max(1, plan.total_weeks || 1)
  const usePhase2 = week > Math.ceil(total / 2) && Array.isArray(plan.week_template_phase2)
  let days = (usePhase2 ? plan.week_template_phase2 : plan.week_template) ?? []
  if (week === 1 && (plan as any).plan_start_date) {
    const startDow = getDayOfWeek((plan as any).plan_start_date)
    days = days.filter(d => d.day_of_week >= startDow)
  }
  return days
}

/**
 * First non-rest day not yet marked done (port of legacy restoreCurrentPosition).
 * Falls back to the plan's first training day when everything is complete.
 */
export function firstIncompletePosition(plan: PlanData, checked: CheckedMap): DayPosition {
  const total = Math.max(1, plan.total_weeks || 1)
  for (let week = 1; week <= total; week++) {
    for (const day of templateForWeek(plan, week)) {
      if (day.is_rest) continue
      if (!checked[dayDoneKey({ week, dayOfWeek: day.day_of_week })]) {
        return { week, dayOfWeek: day.day_of_week }
      }
    }
  }
  const firstTraining = templateForWeek(plan, 1).find(d => !d.is_rest) ?? templateForWeek(plan, 1)[0]
  return { week: 1, dayOfWeek: firstTraining?.day_of_week ?? 1 }
}
